import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { IMAGES, CHURCH_PHOTOS, BELIEFS, VALUE_STATEMENT, MISSION_STATEMENT, VISION_STATEMENT } from "../lib/data";
import { PageHeader } from "../components/PageHeader";
import { Eyebrow } from "../components/ui-kit";
import { Reveal } from "../lib/motion";
import { Marquee } from "../components/Marquee";

export default function About() {
  const [open, setOpen] = useState(0);
  return (
    <div data-testid="about-page">
      <PageHeader eyebrow="Who We Are" title={"About One Love\nMinistries"} sub="One Church. One Family. One Love. Discover the story, mission, and beliefs that shape our church family in Little Rock." image={IMAGES.crowd} />

      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
          <Reveal className="overflow-hidden rounded-sm aspect-[4/5]">
            <img src={CHURCH_PHOTOS.family} alt="One Love Ministries church family" className="w-full h-full object-cover" />
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Our Story</Eyebrow>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05]">Built on faith. Grown in love.</h2>
            <p className="mt-6 text-black/70 text-lg leading-relaxed">One Love Ministries began with a simple, powerful conviction: that the love of Christ has the power to unite people across every background, generation, and walk of life.</p>
            <p className="mt-4 text-black/70 text-lg leading-relaxed">What started as a gathering of a few faithful hearts has grown into a vibrant church family in Little Rock — a community devoted to worship, discipleship, and reaching our city with hope. This is a living story, and your chapter is still being written.</p>
          </Reveal>
        </div>
      </section>

      {/* Mission + Vision */}
      <section className="bg-[#0A0A0A] text-white grain relative">
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <Reveal className="max-w-4xl mb-16">
            <Eyebrow dark>What We Stand On</Eyebrow>
            <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.15]">{VALUE_STATEMENT}</h2>
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 border-t border-white/10 pt-16">
            <Reveal>
              <Eyebrow dark>Our Mission</Eyebrow>
              <p className="text-white/70 text-xl sm:text-2xl leading-relaxed font-display">{MISSION_STATEMENT}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <Eyebrow dark>Our Vision</Eyebrow>
              <p className="text-white/70 text-xl sm:text-2xl leading-relaxed font-display">{VISION_STATEMENT}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee dark />

      {/* Beliefs — Core Principles of Faith */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <Reveal className="mb-14 max-w-2xl">
            <Eyebrow>What We Believe</Eyebrow>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05]">Core Principles of Faith.</h2>
            <p className="mt-5 text-black/60 text-lg">The convictions and Scripture that anchor everything we do.</p>
          </Reveal>
          <div className="border-t border-black/10">
            {BELIEFS.map((b, i) => (
              <div key={b.title} className="border-b border-black/10">
                <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`belief-${i}`}
                  className="w-full flex items-center justify-between gap-4 py-6 text-left group">
                  <span className="flex items-baseline gap-4">
                    <span className="font-display font-extrabold text-[#E63946] text-lg">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-display font-bold text-xl sm:text-2xl group-hover:text-[#E63946] transition-colors">{b.title}</span>
                  </span>
                  <ChevronDown className={`text-[#E63946] shrink-0 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {open === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="pb-8 pl-0 sm:pl-10 max-w-3xl">
                        <p className="text-black/70 text-lg leading-relaxed mb-5">{b.text}</p>
                        <div className="space-y-3">
                          {b.verses.map((v) => (
                            <div key={v.ref} className="border-l-2 border-[#E63946] pl-4">
                              <p className="text-black/70 italic leading-relaxed">“{v.quote}”</p>
                              <p className="text-sm font-medium text-[#E63946] mt-1">{v.ref}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
